import { Link, useParams } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ArrowRight, Star, MapPin, Award, Users, Clock, Play } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { API } from '@/lib/api';
import { HreflangTags, StructuredData, buildOrganizationSchema, buildReviewSchema, PageSEO } from '@/components/SEOComponents';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1800&q=80';
const ABOUT_IMAGE = 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1200&q=80';

const STAT_ICONS = { users: Users, map_pin: MapPin, award: Award, star: Star };

// Fallback category data (used only if CMS block not loaded yet)
const FALLBACK_CATEGORY_IMAGES = {
  diploma: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=800&q=80',
  certificate: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800&q=80',
  masterclass: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80',
  seminar: 'https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?w=800&q=80',
};
const FALLBACK_CATEGORY_INFO = {
  el: { diploma: { title: 'Διπλώματα' }, certificate: { title: 'Πιστοποιητικά' }, masterclass: { title: 'Masterclasses' }, seminar: { title: 'Σεμινάρια' } },
  en: { diploma: { title: 'Diplomas' }, certificate: { title: 'Certificates' }, masterclass: { title: 'Masterclasses' }, seminar: { title: 'Seminars' } },
};

export default function HomePage() {
  const { locale } = useParams();
  const { t } = useLocale();
  const [programs, setPrograms] = useState([]);
  const [articles, setArticles] = useState([]);
  const [blocks, setBlocks] = useState({});
  const [testimonials, setTestimonials] = useState([]);

  const loc = locale || 'en';

  useEffect(() => {
    api.get(`/programs?locale=${loc}`).then(res => setPrograms(res.data.programs || [])).catch(() => {});
    fetch(`${API}/api/public/magazine/articles?limit=3&locale=${loc}`)
      .then(r => r.json()).then(data => setArticles(data.articles || [])).catch(() => {});
    fetch(`${API}/api/public/site-content?locale=${loc}`)
      .then(r => r.json()).then(data => setBlocks(data.blocks || {})).catch(() => {});
    fetch(`${API}/api/public/testimonials?locale=${loc}`)
      .then(r => r.json()).then(data => setTestimonials(data.testimonials || [])).catch(() => {});
  }, [loc]);

  const b = (key, field) => blocks[key]?.content?.[field] || '';

  // Build stats with locale-aware labels
  const statsItems = blocks.stats?.meta?.items || [];
  const statsLabels = blocks.stats?.localized_labels || blocks.stats?.content?.item_labels || [];

  // Build categories from programs
  const categories = {};
  programs.forEach(p => {
    const type = p.program_type || 'other';
    if (!categories[type]) categories[type] = { type, count: 0 };
    categories[type].count++;
  });
  const categoryList = Object.values(categories);

  // CMS-driven category data
  const cmsCatBlock = blocks.program_categories;
  const cmsCatContent = cmsCatBlock?.content || {};
  const cmsCatMeta = cmsCatBlock?.meta || {};
  const cmsCatOrder = cmsCatMeta.order || [];
  const cmsCatImages = cmsCatMeta.images || FALLBACK_CATEGORY_IMAGES;

  const getCategoryInfo = (type) => {
    // Try CMS data first, then fallback
    const cmsInfo = cmsCatContent[type];
    if (cmsInfo) return cmsInfo;
    const fallback = FALLBACK_CATEGORY_INFO[loc] || FALLBACK_CATEGORY_INFO.en || {};
    return fallback[type] || { title: type, description: '' };
  };
  const getCategoryImage = (type) => cmsCatImages[type] || FALLBACK_CATEGORY_IMAGES[type] || FALLBACK_CATEGORY_IMAGES.certificate;

  return (
    <div data-testid="home-page">
      <PageSEO
        title={`MISEL GROUP MASTERCLASS — ${b('hero', 'title') || t('home.hero_title')}`}
        description={loc === 'el' ? 'Η διεθνής ακαδημία κομμωτικής τέχνης. 70 χρόνια εμπειρίας, 18.000+ απόφοιτοι. Προγράμματα ΣΑΕΚ, σεμινάρια, masterclasses.' : 'The international hair education academy where artistry meets precision. 70 years of experience, 18,000+ graduates.'}
        path=""
      />
      <StructuredData data={buildOrganizationSchema()} />
      {testimonials.length > 0 && <StructuredData data={buildReviewSchema(testimonials)} />}

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-end pb-20 md:pb-32 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={blocks.hero?.meta?.image_url || HERO_IMAGE} alt="MISEL GROUP MASTERCLASS" className="img-editorial" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 w-full">
          <p className="caption-text animate-fade-up text-gold mb-4">{b('hero', 'label') || 'MISEL GROUP MASTERCLASS'}</p>
          <h1 className="heading-xl animate-fade-up delay-100 max-w-5xl">{b('hero', 'title') || t('home.hero_title')}</h1>
          <p className="font-accent italic text-xl md:text-2xl text-muted-foreground mt-6 animate-fade-up delay-200 max-w-3xl">
            {b('hero', 'subtitle') || t('home.hero_subtitle')}
          </p>
          <div className="flex flex-wrap gap-4 mt-10 animate-fade-up delay-300">
            <Link to={`/${loc}/admissions`} className="btn-gold" data-testid="hero-cta-apply">
              {t('home.cta_apply')} <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
            </Link>
            <Link to={`/${loc}/contact`} className="btn-secondary border-2 border-white text-white bg-black/30 hover:bg-black/50 backdrop-blur-sm" data-testid="hero-cta-consult">
              {t('home.cta_consult')}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-16 bg-foreground text-white" data-testid="stats-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
          {statsItems.length > 0 ? (
            statsItems.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((stat, i) => {
              const Icon = STAT_ICONS[stat.icon] || Users;
              const localLabel = statsLabels[i] || stat.label;
              return (
                <div key={i} className="animate-fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`stat-${i}`}>
                  <Icon size={24} className="mx-auto mb-3 text-gold" strokeWidth={1.5} />
                  <div className="text-3xl md:text-4xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{stat.value}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">{localLabel}</div>
                </div>
              );
            })
          ) : (
            [{v: '2,500+', l: t('home.stats_students'), i: Users}, {v: '40+', l: t('home.stats_countries'), i: MapPin}, {v: '15+', l: t('home.stats_years'), i: Award}, {v: '98%', l: t('home.stats_satisfaction'), i: Star}].map((s, i) => {
              const Icon = s.i;
              return (
                <div key={i} className="animate-fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`stat-${i}`}>
                  <Icon size={24} className="mx-auto mb-3 text-gold" strokeWidth={1.5} />
                  <div className="text-3xl md:text-4xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{s.v}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">{s.l}</div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ===== PROGRAM CATEGORIES ===== */}
      <section className="section-padding" data-testid="categories-section">
        <div className="max-w-[1600px] mx-auto">
          <p className="caption-text text-gold mb-4 animate-fade-up">{b('programs_section', 'label') || t('home.programs_subtitle')}</p>
          <h2 className="heading-lg animate-fade-up delay-100">{b('programs_section', 'title') || t('home.programs_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {(cmsCatOrder.length > 0 ? cmsCatOrder : categoryList.map(c => c.type)).map((catType, i) => {
              const info = getCategoryInfo(catType);
              const cat = categories[catType] || { count: 0 };
              if (cat.count === 0 && cmsCatOrder.length === 0) return null;
              return (
                <Link key={catType} to={`/${loc}/programs?type=${catType}`}
                  className={`card-glass group animate-fade-up delay-${(i + 2) * 100}`}
                  data-testid={`category-${catType}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-t-2xl bg-zinc-100">
                    <img src={getCategoryImage(catType)} alt={info.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="heading-sm mb-2 group-hover:text-gold transition-colors">{info.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{info.description}</p>
                    {cat.count > 0 && <span className="text-xs text-gold font-medium">{cat.count} {(() => {
                      const PROGRAM_WORDS = { el: ['πρόγραμμα','προγράμματα'], en: ['program','programs'], fr: ['programme','programmes'], es: ['programa','programas'], de: ['Programm','Programme'], pt: ['programa','programas'], tr: ['program','program'], it: ['programma','programmi'], ru: ['программа','программы'], sq: ['program','programe'], bg: ['програма','програми'], zh: ['项目','项目'], hi: ['कार्यक्रम','कार्यक्रम'], ar: ['برنامج','برامج'] };
                      const words = PROGRAM_WORDS[loc] || PROGRAM_WORDS.en;
                      return cat.count === 1 ? words[0] : words[1];
                    })()}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== WHY MISEL ===== */}
      <section className="section-padding bg-foreground/[0.02]" data-testid="why-section">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-up">
            <p className="caption-text text-gold mb-4">{b('why_section', 'label') || t('home.why_subtitle')}</p>
            <h2 className="heading-lg mb-8">{b('why_section', 'title') || t('home.why_title')}</h2>
            <div className="space-y-6">
              {[
                { titleKey: 'home.why_precision_title', descKey: 'home.why_precision_desc' },
                { titleKey: 'home.why_artistry_title', descKey: 'home.why_artistry_desc' },
                { titleKey: 'home.why_international_title', descKey: 'home.why_international_desc' },
                { titleKey: 'home.why_salon_title', descKey: 'home.why_salon_desc' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-1 bg-gold rounded-full flex-shrink-0 mt-1" style={{ height: '3rem' }} />
                  <div>
                    <h4 className="font-semibold text-base mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{t(item.titleKey)}</h4>
                    <p className="text-sm text-muted-foreground">{t(item.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="animate-fade-up delay-200"><div className="rounded-3xl overflow-hidden"><img src={blocks.why_misel?.meta?.image_url || ABOUT_IMAGE} alt="MISEL" className="img-editorial aspect-[4/5]" /></div></div>
        </div>
      </section>

      {/* ===== TESTIMONIALS (Max 3 + See More) ===== */}
      {testimonials.length > 0 && (
      <section className="section-padding" data-testid="testimonials-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="caption-text text-gold mb-4 animate-fade-up">{b('testimonials_section', 'label') || t('nav.testimonials')}</p>
              <h2 className="heading-lg animate-fade-up delay-100">{b('testimonials_section', 'title') || t('home.testimonials_title')}</h2>
            </div>
            {testimonials.length > 3 && (
              <Link to={`/${loc}/testimonials`} className="hidden md:flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors animate-fade-up delay-200" data-testid="testimonials-see-more">
                {t('home.magazine_view_all') || 'See More'} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((test, i) => (
              <div key={test.id} className={`card-glass p-5 sm:p-8 animate-fade-up delay-${(i + 2) * 100}`} data-testid={`testimonial-${i}`}>
                <p className="font-accent italic text-base sm:text-lg leading-relaxed text-foreground/80 mb-6 break-words">"{test.testimonial_text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                  {test.author_photo ? (
                    <img src={test.author_photo} alt={test.author_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center text-sm font-bold">{test.author_name?.[0]}</div>
                  )}
                  <div>
                    <p className="font-semibold text-sm">{test.author_name}</p>
                    <p className="text-xs text-muted-foreground">{test.author_title}</p>
                    <p className="text-[10px] text-gold">{test.country}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {testimonials.length > 3 && (
            <div className="md:hidden mt-8 text-center">
              <Link to={`/${loc}/testimonials`} className="btn-secondary inline-flex" data-testid="testimonials-see-more-mobile">
                {t('home.magazine_view_all') || 'See More'} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
              </Link>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ===== MAGAZINE / LATEST ARTICLES ===== */}
      {articles.length > 0 && (
        <section className="section-padding bg-foreground/[0.02]" data-testid="magazine-section">
          <div className="max-w-[1600px] mx-auto">
            <p className="caption-text text-gold mb-4 animate-fade-up">{b('magazine_section', 'label') || t('nav.magazine')}</p>
            <div className="flex items-end justify-between mb-12">
              <h2 className="heading-lg animate-fade-up delay-100">{b('magazine_section', 'title') || t('home.magazine_title')}</h2>
              <Link to={`/${loc}/magazine`} className="hidden md:flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors animate-fade-up delay-200" data-testid="magazine-view-all">
                {t('home.magazine_view_all')} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((art, i) => (
                <Link key={art.id} to={`/${loc}/magazine/${art.slug}`}
                  className={`group animate-fade-up delay-${(i + 2) * 100}`} data-testid={`home-article-${art.slug}`}>
                  <div className="card-glass overflow-hidden">
                    <div className="aspect-[16/9] bg-zinc-100 overflow-hidden relative">
                      {art.cover_image && <img src={art.cover_image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      {art.post_type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg"><Play size={14} className="text-foreground ml-0.5" /></div>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="heading-sm mb-2 mt-2 line-clamp-2 group-hover:text-gold transition-colors">{art.title}</h3>
                      {art.excerpt && <p className="text-sm text-muted-foreground line-clamp-2">{art.excerpt}</p>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FINAL CTA ===== */}
      <section className="section-padding bg-foreground text-white text-center" data-testid="cta-section">
        <div className="max-w-4xl mx-auto animate-fade-up">
          <p className="caption-text text-gold mb-4">{b('cta_section', 'label') || t('home.cta_begin')}</p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
            {b('cta_section', 'title') || t('home.cta_title')}
          </h2>
          <p className="font-accent italic text-xl text-white/60 mb-10">
            {b('cta_section', 'subtitle') || t('home.cta_subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={`/${loc}/admissions`} className="btn-gold" data-testid="cta-apply">{t('common.apply_now')} <ArrowRight size={16} className="ml-2" strokeWidth={1.5} /></Link>
            <Link to={`/${loc}/programs`} className="btn-secondary border-white/30 text-white hover:bg-white/10">{t('common.view_programs')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
